import React, { useState } from 'react';
import clsx from 'clsx';
import { Button } from '../common/Catalyst/button';
import {
  AmenityReturn,
  AmenitySeasonCreate,
  AmenitySeasonReturn,
} from '../clients/rust-api-client';
import { amenityService } from '../clients/ApiClients';
import {
  formatSeasonRange,
  reservationErrorMessage,
} from '../reservation/reservationMeta';
import { helpClass, inputClass, labelClass } from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { FieldPicker } from '../common/Custom/FieldPicker';

/**
 * An amenity's seasons (P2, #642): named stretches of the year — "Pool
 * season, May 25 – Sep 7" — that can carry their own opening hours on the
 * Hours tab. A season whose end comes before its start wraps the new year
 * ("Ski season, Dec 1 – Mar 31"). Seasons may not overlap; the server
 * refuses one that does.
 *
 * Outside every season the amenity follows its default hours, and with
 * seasons but no default hours it is **closed** — which is how a pool
 * that only opens in summer is set up.
 */
interface Props {
  amenity: AmenityReturn;
  readOnly: boolean;
  onChanged: () => Promise<void> | void;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const blank: AmenitySeasonCreate = {
  name: '',
  start_month: 5,
  start_day: 25,
  end_month: 9,
  end_day: 7,
};

/** Whether a season as typed is complete: a name and real days of the year. */
export const seasonDraftOk = (s: AmenitySeasonCreate): boolean =>
  s.name.trim().length > 0 &&
  s.start_month >= 1 &&
  s.start_month <= 12 &&
  s.end_month >= 1 &&
  s.end_month <= 12 &&
  s.start_day >= 1 &&
  s.start_day <= DAYS_IN_MONTH[s.start_month - 1] &&
  s.end_day >= 1 &&
  s.end_day <= DAYS_IN_MONTH[s.end_month - 1];

const MonthDay: React.FC<{
  label: string;
  month: number;
  day: number;
  onChange: (month: number, day: number) => void;
}> = ({ label, month, day, onChange }) => (
  <fieldset className="flex flex-col gap-1">
    <legend className="text-[12px] font-medium text-fg-muted">{label}</legend>
    <div className="flex gap-1.5">
      <FieldPicker
        size="sm"
        className="w-32"
        label={`${label} month`}
        value={String(month)}
        onChange={(v) => {
          const m = Number(v);
          onChange(m, Math.min(day, DAYS_IN_MONTH[m - 1]));
        }}
        options={MONTH_NAMES.map((name, i) => ({ value: String(i + 1), label: name }))}
      />
      <FieldPicker
        size="sm"
        className="w-20"
        label={`${label} day`}
        value={String(day)}
        onChange={(v) => onChange(month, Number(v))}
        options={Array.from({ length: DAYS_IN_MONTH[month - 1] }, (_, i) => i + 1).map((d) => ({
          value: String(d),
          label: String(d),
        }))}
      />
    </div>
  </fieldset>
);

const SeasonForm: React.FC<{
  initial: AmenitySeasonCreate;
  submitLabel: string;
  onSubmit: (s: AmenitySeasonCreate) => Promise<void>;
  onCancel?: () => void;
}> = ({ initial, submitLabel, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState<AmenitySeasonCreate>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = seasonDraftOk(draft);

  return (
    <form
      className="space-y-3 rounded-lg border border-line px-3 py-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ok) return;
        setSaving(true);
        setError(null);
        try {
          await onSubmit({ ...draft, name: draft.name.trim() });
        } catch (err) {
          setError(reservationErrorMessage(err, "Couldn't save the season."));
        } finally {
          setSaving(false);
        }
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className={labelClass}>
          Name
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Pool season"
            className={clsx(inputClass, 'w-48')}
          />
        </label>
        <MonthDay
          label="From"
          month={draft.start_month}
          day={draft.start_day}
          onChange={(m, d) => setDraft({ ...draft, start_month: m, start_day: d })}
        />
        <MonthDay
          label="Through"
          month={draft.end_month}
          day={draft.end_day}
          onChange={(m, d) => setDraft({ ...draft, end_month: m, end_day: d })}
        />
      </div>
      <ErrorAlert>{error}</ErrorAlert>
      <div className="flex gap-2">
        <Button color="sky" type="submit" disabled={!ok || saving}>
          {saving ? 'Saving…' : submitLabel}
        </Button>
        {onCancel && (
          <Button plain type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export const AmenitySeasons: React.FC<Props> = ({ amenity, readOnly, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seasons = [...(amenity.seasons ?? [])].sort(
    (a, b) => a.start_month - b.start_month || a.start_day - b.start_day
  );

  const remove = async (s: AmenitySeasonReturn) => {
    if (!window.confirm(`Delete ${s.name}? Its own opening hours are deleted with it.`)) return;
    setError(null);
    try {
      await amenityService.deleteAmenitySeason({ amenityId: amenity.id, seasonId: s.id });
      await onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't delete the season."));
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <p className={helpClass}>
        A season can have its own opening hours (Hours tab). Outside every season the default hours
        apply — and with seasons but no default hours, the amenity is closed out of season.
      </p>
      {seasons.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No seasons — the same hours all year.</p>
      ) : (
        <ul className="divide-y divide-line/60 rounded-lg border border-line">
          {seasons.map((s) =>
            editing === s.id ? (
              <li key={s.id} className="p-2">
                <SeasonForm
                  initial={{
                    name: s.name,
                    start_month: s.start_month,
                    start_day: s.start_day,
                    end_month: s.end_month,
                    end_day: s.end_day,
                  }}
                  submitLabel="Save season"
                  onCancel={() => setEditing(null)}
                  onSubmit={async (draft) => {
                    await amenityService.updateAmenitySeason({
                      amenityId: amenity.id,
                      seasonId: s.id,
                      amenitySeasonCreate: draft,
                    });
                    setEditing(null);
                    await onChanged();
                  }}
                />
              </li>
            ) : (
              <li key={s.id} className="flex items-center gap-3 px-3 py-2.5 text-[13px]">
                <span className="font-medium text-fg">{s.name}</span>
                <span className="text-fg-muted">{formatSeasonRange(s)}</span>
                {!readOnly && (
                  <span className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing(s.id)}
                      className="text-[12px] text-accent-fg hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(s)}
                      className="text-[12px] text-danger hover:underline"
                    >
                      Delete
                    </button>
                  </span>
                )}
              </li>
            )
          )}
        </ul>
      )}
      <ErrorAlert>{error}</ErrorAlert>
      {!readOnly &&
        (adding ? (
          <SeasonForm
            initial={blank}
            submitLabel="Add season"
            onCancel={() => setAdding(false)}
            onSubmit={async (draft) => {
              await amenityService.createAmenitySeason({
                amenityId: amenity.id,
                amenitySeasonCreate: draft,
              });
              setAdding(false);
              await onChanged();
            }}
          />
        ) : (
          <Button outline onClick={() => setAdding(true)}>
            Add season
          </Button>
        ))}
    </div>
  );
};

export default AmenitySeasons;
